import {
  BodyText,
  Button,
  Checkbox,
  Heading,
  Icon,
  IconSet,
  Input,
  SelectMenu,
  Tooltip,
} from '@postscript/components';
import { Field, Form } from 'formik';
import { useEffect, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import styled from 'styled-components';

import {
  FILTER_KEYS,
  OUTCOMES_OPTIONS,
  SEARCH_DIRECTION,
  SENTIMENT_OPTIONS,
  ShopperInsightsControls,
  SORT_BY,
  STATUS_OPTIONS,
  SUMMARY_OPTIONS,
} from '../pages/types';

const StyledSearchModalBackdrop = styled.div<{ isOpen: boolean }>`
  ${({ isOpen }) =>
    isOpen
      ? `
          display: flex;
          opacity: 50%;
        `
      : `
          display: none;
          opacity: 0;
        `}
  transition: all var(--hover-transition);
  transition-behavior: allow-discrete;

  height: 100vh;
  width: 100vw;
  background: var(--black);

  position: fixed;
  top: 0;
  left: 0;
  z-index: 1;
`;

const StyledSearchModal = styled.div<{
  isOpen: boolean;
  parentRef?: React.RefObject<HTMLDivElement>;
}>`
  ${({ isOpen }) =>
    isOpen
      ? `
          display: flex;
          opacity: 1;
        `
      : `
          display: none;
          opacity: 0;
        `}
  transition: display all var(--hover-transition);
  transition-behavior: allow-discrete;

  flex-direction: column;
  gap: var(--spacing-3);

  border-radius: var(--border-radius-medium);
  background: var(--surface-bkg-color);
  box-shadow: var(--box-shadow-large);
  padding: var(--spacing-3);

  /* We want the modal to neatly overlay its parent, but it's rendered in a portal,
   * so absolute positioning with relative values will not work! Instead,
   * we can read the parent's coordinates via a ref passed down as a prop.
   */
  position: absolute;
  ${({ parentRef }) => {
    const boundingRect = parentRef?.current?.getBoundingClientRect();
    if (boundingRect) {
      const { top, left, width } = boundingRect;
      return `
        top: ${top - 6}px;
        left: ${left - 6}px;
        width: ${width}px;
      `;
    }
  }}

  z-index: 2;

  > form {
    display: flex;
    flex-direction: column;
    gap: var(--spacing-2);
  }
`;

const StyledSearchHeader = styled.header`
  display: flex;
  flex-direction: column;
  gap: 3px;
`;

const SearchHeaderFirstLine = styled.div`
  display: flex;
  gap: var(--spacing-1);
`;

const SearchHeaderBadges = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: var(--spacing-1);
  padding-left: var(--spacing-5);
`;

const StyledTitle = styled.div`
  display: flex;
  gap: var(--spacing-1);
  align-items: center;
  flex: 1 1;
`;

const StyledSearchPrimary = styled.div`
  display: flex;
  gap: var(--spacing-2);
  align-items: flex-end;
  flex-wrap: wrap;

  & > div:first-child {
    flex: 1 0 240px;
  }

  & > div:last-child {
    flex: 0 1 204px;
  }
`;

const StyledFilterContainer = styled.div`
  display: flex;
  gap: var(--spacing-2);
  flex-wrap: wrap;
`;

const StyledTextDivider = styled.div`
  height: var(--spacing-6);
  display: flex;
  align-items: center;
`;

const StyledFilters = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--spacing-2);
  flex: 1 0 276px;
`;

const StyledButtons = styled.div`
  display: flex;
  gap: var(--spacing-1);
  justify-content: flex-end;
  padding-top: var(--spacing-2);
`;

interface ConversationIntelligenceControlsFormProps {
  values: any;
  setFieldValue: any;
  submitForm: any;
  resetForm: any;
  controls: ShopperInsightsControls;
  isLoading: boolean;
  isMultiShop: boolean;
  isOpen: boolean;
  toggleFunction: any;
  fullClearFunction?: any;
  parentRef?: React.RefObject<HTMLDivElement>;
  badgeElements?: any;
  shopOptions: any[];
  agentOptions: any[];
  setShopSearchText: (text: string) => void;
  setAgentSearchText: (text: string) => void;
}

const ConversationIntelligenceControlsForm = ({
  values,
  setFieldValue,
  submitForm,
  resetForm,
  controls,
  isLoading,
  isMultiShop,
  isOpen,
  toggleFunction,
  fullClearFunction,
  parentRef,
  badgeElements,
  shopOptions,
  agentOptions,
  setShopSearchText,
  setAgentSearchText,
}: ConversationIntelligenceControlsFormProps) => {
  const modalRef = useRef<HTMLDivElement>(null);

  const updatedFilters = useMemo(() => {
    const { filters } = controls;

    // Determine status
    let status = null;
    if (filters.includes(FILTER_KEYS.LIVE))
      status = STATUS_OPTIONS[FILTER_KEYS.LIVE];
    else if (filters.includes(FILTER_KEYS.INACTIVE))
      status = STATUS_OPTIONS[FILTER_KEYS.INACTIVE];

    // Determine summary - default to "Has Summary" unless "No Summary" is explicitly set
    let summary = SUMMARY_OPTIONS[FILTER_KEYS.HAS_SUMMARY];
    if (filters.includes(FILTER_KEYS.NO_SUMMARY))
      summary = SUMMARY_OPTIONS[FILTER_KEYS.NO_SUMMARY];

    // Determine outcomes (multiselect)
    const outcomes = filters
      .filter((filter) =>
        [
          FILTER_KEYS.CONVERTED,
          FILTER_KEYS.NOT_CONVERTED,
          FILTER_KEYS.UNSUBSCRIBED,
        ].includes(filter),
      )
      .map((filter) => OUTCOMES_OPTIONS[filter]);

    // Determine sentiment (single select)
    let sentiment = null;
    if (filters.includes(FILTER_KEYS.POSITIVE))
      sentiment = SENTIMENT_OPTIONS[FILTER_KEYS.POSITIVE];
    else if (filters.includes(FILTER_KEYS.NEUTRAL))
      sentiment = SENTIMENT_OPTIONS[FILTER_KEYS.NEUTRAL];
    else if (filters.includes(FILTER_KEYS.NEGATIVE))
      sentiment = SENTIMENT_OPTIONS[FILTER_KEYS.NEGATIVE];

    return { status, summary, outcomes, sentiment };
  }, [controls.filters]);

  useEffect(() => {
    // If "Happening now" is selected, set summary to "No Summary" and clear sentiment
    if (updatedFilters.status?.value === FILTER_KEYS.LIVE) {
      setFieldValue('summary', SUMMARY_OPTIONS[FILTER_KEYS.NO_SUMMARY]);
      setFieldValue('sentiment', null);
    } else {
      setFieldValue('status', updatedFilters.status);
      setFieldValue('summary', updatedFilters.summary);
      setFieldValue('sentiment', updatedFilters.sentiment);
    }
    setFieldValue('outcomes', updatedFilters.outcomes);
  }, [updatedFilters, setFieldValue]);

  // Check if "Happening now" is selected to disable other groups
  const isHappeningNowSelected = values.status?.value === FILTER_KEYS.LIVE;

  return createPortal(
    <>
      <StyledSearchModal
        aria-labelledby="ci-search-modal-title"
        aria-modal="true"
        id="ci-search-modal"
        isOpen={isOpen}
        ref={modalRef}
        role="dialog"
        parentRef={parentRef}
      >
        <StyledSearchHeader>
          <SearchHeaderFirstLine>
            <StyledTitle>
              <Icon
                color="var(--text-color)"
                component={IconSet.Search}
                size={24}
              />
              <Heading size="xx-small" id="ci-search-modal-title">
                Search and filter conversations
              </Heading>
            </StyledTitle>
            <Button
              icon={IconSet.Close}
              monochrome
              onClick={() => toggleFunction(false)}
              size="small"
              variant="text"
            />
          </SearchHeaderFirstLine>
          <SearchHeaderBadges>{badgeElements}</SearchHeaderBadges>
        </StyledSearchHeader>

        <Form>
          <StyledSearchPrimary>
            <Field
              as={Input}
              label="Search for"
              placeholder="Enter a keyword or phrase"
              icon={IconSet.Search}
              name="searchText"
              onChange={({ target }: any) => {
                setFieldValue('searchText', target.value);
              }}
              type="text"
              fieldWidth="100%"
            />
            <StyledTextDivider>
              <BodyText bold>in</BodyText>
            </StyledTextDivider>
            <div
              style={{
                display: 'flex',
                alignItems: 'flex-end',
                gap: 'var(--spacing-2)',
                minWidth: '300px',
                flex: '0 0 auto',
              }}
            >
              <Field
                as={SelectMenu}
                label="Message type"
                isClearable={false}
                placeholder="Search Direction"
                name="searchDirection"
                value={values.searchDirection}
                options={Object.values(SEARCH_DIRECTION)}
                onChange={(value: any) => {
                  setFieldValue('searchDirection', value);
                }}
              />
              <Checkbox
                id="onlyShopper"
                label="Only Shopper"
                checked={values.onlyShopper}
                onChange={({ target: { checked } }) =>
                  setFieldValue('onlyShopper', checked)
                }
                style={{ marginBottom: 'var(--spacing-1)' }}
                fieldSize="medium"
              />
            </div>
          </StyledSearchPrimary>

          <StyledFilterContainer>
            <StyledFilters>
              <Field
                as={SelectMenu}
                label="Status"
                placeholder="Select status"
                name="status"
                isClearable
                value={values.status}
                options={Object.values(STATUS_OPTIONS)}
                onChange={(value: any) => {
                  setFieldValue('status', value);
                  // If "Happening now" is selected, set summary to "No Summary" and clear other filters
                  if (value?.value === FILTER_KEYS.LIVE) {
                    setFieldValue(
                      'summary',
                      SUMMARY_OPTIONS[FILTER_KEYS.NO_SUMMARY],
                    );
                    setFieldValue('outcomes', []);
                    setFieldValue('sentiment', null);
                  } else {
                    // If switching away from "Happening now", reset summary to "Has Summary"
                    setFieldValue(
                      'summary',
                      SUMMARY_OPTIONS[FILTER_KEYS.HAS_SUMMARY],
                    );
                  }
                }}
                fieldWidth="100%"
              />

              <div
                aria-describedby="summary-tooltip"
                data-event={null}
                data-for="summary-tooltip"
                data-tip
                role="button"
                tabIndex={0}
              >
                <Field
                  as={SelectMenu}
                  label="Summary"
                  placeholder="Select summary"
                  name="summary"
                  isClearable={false}
                  value={values.summary}
                  options={Object.values(SUMMARY_OPTIONS)}
                  disabled={isHappeningNowSelected}
                  onChange={(value: any) => {
                    setFieldValue('summary', value);
                  }}
                  fieldWidth="100%"
                />
                <Tooltip
                  id="summary-tooltip"
                  disable={!isHappeningNowSelected}
                  place="bottom"
                >
                  Summary is not available when filtering by live conversations.
                </Tooltip>
              </div>
            </StyledFilters>

            <StyledFilters>
              <div
                aria-describedby="outcomes-tooltip"
                data-event={null}
                data-for="outcomes-tooltip"
                data-tip
                role="button"
                tabIndex={0}
              >
                <Field
                  as={SelectMenu}
                  label="Outcomes"
                  placeholder="Select outcomes"
                  name="outcomes"
                  isMulti
                  value={values.outcomes}
                  options={Object.values(OUTCOMES_OPTIONS)}
                  disabled={isHappeningNowSelected}
                  onChange={(value: any) => {
                    setFieldValue('outcomes', value || []);
                  }}
                  fieldWidth="100%"
                />
                <Tooltip
                  id="outcomes-tooltip"
                  disable={!isHappeningNowSelected}
                  place="bottom"
                >
                  Outcomes are not available when filtering by live
                  conversations.
                </Tooltip>
              </div>

              <div
                aria-describedby="sentiment-tooltip"
                data-event={null}
                data-for="sentiment-tooltip"
                data-tip
                role="button"
                tabIndex={0}
              >
                <Field
                  as={SelectMenu}
                  label="Sentiment"
                  placeholder="Select sentiment"
                  name="sentiment"
                  isClearable
                  value={values.sentiment}
                  options={Object.values(SENTIMENT_OPTIONS)}
                  disabled={isHappeningNowSelected}
                  onChange={(value: any) => {
                    setFieldValue('sentiment', value);
                  }}
                  fieldWidth="100%"
                />
                <Tooltip
                  id="sentiment-tooltip"
                  disable={!isHappeningNowSelected}
                  place="bottom"
                >
                  Sentiment is not available when filtering by live
                  conversations.
                </Tooltip>
              </div>
            </StyledFilters>
          </StyledFilterContainer>

          <StyledFilterContainer>
            <StyledFilters>
              <Field
                as={SelectMenu}
                label="Sort by"
                isClearable={false}
                placeholder="Sort"
                name="sortBy"
                value={values.sortBy}
                options={Object.values(SORT_BY)}
                onChange={(value: any) => {
                  setFieldValue('sortBy', value);
                }}
                fieldWidth="100%"
              />
            </StyledFilters>
          </StyledFilterContainer>

          {isMultiShop && (
            <StyledFilterContainer>
              <StyledFilters>
                <Field
                  as={SelectMenu}
                  label="Filter by shop"
                  placeholder="Select shops"
                  name="shops"
                  isMulti
                  value={values.shops}
                  options={shopOptions}
                  onChange={(value: any) => {
                    setFieldValue('shops', value);
                  }}
                  onInputChange={setShopSearchText}
                  fieldWidth="100%"
                />
                <Field
                  as={SelectMenu}
                  label="Filter by agent"
                  placeholder="Select agents"
                  name="agents"
                  isMulti
                  value={values.agents}
                  options={agentOptions}
                  onChange={(value: any) => {
                    setFieldValue('agents', value);
                  }}
                  onInputChange={setAgentSearchText}
                  fieldWidth="100%"
                />
              </StyledFilters>
            </StyledFilterContainer>
          )}

          {/* Filter, Reset */}
          <StyledButtons>
            <Button
              variant="secondary"
              monochrome
              onClick={() => {
                resetForm();
                setFieldValue('searchText', '');
                setFieldValue('status', null);
                setFieldValue(
                  'summary',
                  SUMMARY_OPTIONS[FILTER_KEYS.HAS_SUMMARY],
                );
                setFieldValue('outcomes', []);
                setFieldValue('sentiment', null);
                setFieldValue('onlyShopper', true);
                if (fullClearFunction) {
                  fullClearFunction({
                    ...controls,
                    topicLabel: undefined,
                    tag: undefined,
                  });
                }
                submitForm();
              }}
            >
              Clear
            </Button>
            <Button type="submit" disabled={isLoading} monochrome>
              Search
            </Button>
          </StyledButtons>
        </Form>
      </StyledSearchModal>
      <StyledSearchModalBackdrop
        isOpen={isOpen}
        onClick={() => toggleFunction(false)}
      />
    </>,
    document.body,
  );
};

export default ConversationIntelligenceControlsForm;
